import { Resources } from 'constants/resources';

import { useEffect, useState } from 'react';

import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useEditController, useNotify, useRedirect } from 'react-admin';
import { CancelButton, SaveButtonValidated, DeleteButton } from 'shared/mui/Buttons';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { EditGuesserPage } from 'shared/react-admin/Pages/EditGuesserPage/EditGuesserPage';
import { Permission } from 'types/embedded/permission';
import { User } from 'types/embedded/user';

import { UsersEditForm } from './UsersEditForm';
import { UsersEditSkeleton } from './UsersEditSkeleton';
import { UsersDeleteModal } from '../components/UsersDeleteModal';

export const UsersEdit = () => {
  const { record } = useEditController<User>();
  const notify = useNotify();
  const redirect = useRedirect();
  const { hasResourcePermissions } = usePermissions();
  const [hasCorporateEmail, setHasCorporateEmail] = useState(false);
  const { handleCloseRecordModal, openRecordModal, handleOpenRecordModal } = useRecordModal<User>();

  const transform = (data: User) => {
    return {
      ...data,
      merchantAccesses: data.merchantAccesses.map((merchantAccess) => merchantAccess?.['@id']),
      corporateEmail: hasCorporateEmail ? data.corporateEmail : null,
      roleEntities: data.roleEntities,
    };
  };

  const onSuccess = (user: User) => {
    notify(
      <>
        Пользователь <TypographyBold text={`${user.firstName} ${user.lastName}`} /> изменён
      </>,
      { type: 'success' },
    );
    redirect('list', Resources.Users);
  };

  useEffect(() => {
    setHasCorporateEmail(!!record?.corporateEmail);
  }, [record]);

  return (
    <EditGuesserPage
      leftActionsSlot={
        hasResourcePermissions(Resources.Users, [Permission.Full, Permission.Delete]) ? (
          <DeleteButton onClick={() => handleOpenRecordModal(record)} variant="outlined" />
        ) : undefined
      }
      listName="Пользователи"
      mutationOptions={{ onSuccess, onError: () => undefined }}
      rightActionsSlot={[
        <CancelButton key="cancel-button" linkType="back" variant="outlined" />,
        <SaveButtonValidated key="save-button" label="Сохранить" />,
      ]}
      skeleton={<UsersEditSkeleton />}
      title="Редактирование пользователя"
      transform={transform}
    >
      <UsersEditForm
        hasCorporateEmail={hasCorporateEmail}
        setHasCorporateEmail={setHasCorporateEmail}
      />

      <UsersDeleteModal
        handleCloseRecordModal={handleCloseRecordModal}
        openRecordModal={openRecordModal}
        record={record}
      />
    </EditGuesserPage>
  );
};
